import { BotMessageSubtype } from "@/types/chat"
import { ChatServerEventData, MessageChunk } from "@/types/streamed-messages"
import remove from "lodash/remove"

import { AgentApi } from "@/lib/agent-api"
import { buildClientAxios } from "@/lib/client-axios"
import { eventStreamUrl } from "@/lib/event-stream-url"
import { warmFlightSearchCache } from "@/lib/storage/flight-search-slice"
import { warnUnreachable } from "@/lib/utils/assertions"
import {
  EventSmoother,
  NullEventSmoother,
  TimedEventSmoother,
} from "./event-smoother"
import { wait } from "./promise-utils"
import { performBotAction } from "./storage/bot-action-thunks"
import { updateEventsInCache } from "./storage/itinerary-cache-utils"
import {
  considerAskingBotForFollowUp,
  executeImmediateFollowUp,
  temporarilyDisableMessageStreaming,
} from "./storage/message-thunks"
import { AppDispatch } from "./storage/redux-store"
import { showToastsForStreamedEvents } from "./storage/streaming-thunks"
import { selectTripRef } from "./storage/trip-slice"
import userSlice from "./storage/user-slice"
import { StreamedMessageBuilder } from "./streamed-message-builder"

const SMOOTHING_ENABLED = true

/**
 * Listens to the chat stream identified by the given streamId and
 * dispatches bot responses to the userSlice.
 */
export class StreamedMessageListener {
  static readonly MAX_RETRY_COUNT = 1
  static activeListeners: StreamedMessageListener[] = []

  readonly chatId: number
  readonly streamId: string
  eventSource: EventSource
  private snapshotFetchedForSession?: number
  readonly builder: StreamedMessageBuilder
  retryCount = 0
  lastEventId: string | undefined
  private stopRequested = false
  private closed = false
  // Chunks have been folded into the builder that the store has not been told
  // about yet. Never outlives the batch that set it (see onMessage).
  private inProgressMessageStale = false

  // We read events from the server using an EventSource. Each event
  // may contain a word or two, or maybe the name of a hotel with
  // associated json. But the events are sometimes sent with
  // inconsistent time-spacing. For example, the entity_start events
  // that mark the beginning of the name of a hotel or attraction
  // often take much longer to appear than plain text events because
  // of the extra processing required on the back end.
  //
  // Since we show the chunks to the user as they arrive in the
  // client, any pauses before entities creates a disconcerting,
  // herky-jerky experience.
  //
  // Use an EventSmoother to smooth out the flow. Instead of writing
  // the chunks directly into our message builder (which then goes
  // into redux and out to the UI), add the events to the smoother
  // as they arrive from the server and let it space them out.
  smoother: EventSmoother = SMOOTHING_ENABLED
    ? new TimedEventSmoother()
    : new NullEventSmoother()

  static listenToStream(
    chatId: number,
    streamId: string,
    dispatch: AppDispatch,
    sub_type?: BotMessageSubtype
  ) {
    if (!this.activeListeners.some((s) => s.streamId === streamId)) {
      const newListener = new StreamedMessageListener(
        chatId,
        streamId,
        dispatch,
        sub_type
      )
      this.activeListeners.push(newListener)
      return newListener
    }
  }

  static closeActiveListeners({ preserveStreamIds = false } = {}) {
    // close() removes from this array; iterate a snapshot to close every listener.
    this.activeListeners
      .slice()
      .forEach((listener) => listener.close({ preserveStreamIds }))
  }

  static closeActiveListenersExcept(keepStreamIds: string[]) {
    this.activeListeners
      .filter((listener) => !keepStreamIds.includes(listener.streamId))
      .forEach((listener) => listener.close())
  }

  // Settles the in-progress message the moment the user hits stop, without
  // waiting on the backend's terminal event: further in-progress chunks are
  // dropped, the Stop button flips back to Send, and the bubble reaches its
  // predicted final state (the partial answer settles as an interrupted
  // message, or an empty bubble clears outright — mirroring the backend's
  // own keep-partial-vs-cancel decision). The backend's terminal event later
  // reconciles this stream in place with the persisted message.
  static stopStreamingForChat(chatId: number) {
    this.activeListeners
      .filter((listener) => listener.chatId === chatId)
      .forEach((listener) => {
        listener.stopRequested = true
        // Queued deliveries are no-ops once the flag is set; draining them keeps
        // the smoother from idly pacing through dead entries.
        listener.smoother.flushAll()

        listener.dispatch(
          userSlice.actions.markStreamStopped(listener.streamId)
        )
        // Mirror the backend's keep-partial-vs-cancel test (net text blank?):
        // any non-whitespace already streamed persists as an interrupted message.
        if (listener.builder.body.trim().length > 0) {
          listener.dispatch(
            userSlice.actions.settleStoppedInProgressMessage(listener.streamId)
          )
        } else {
          listener.dispatch(
            userSlice.actions.removeInProgressMessageIfPresent(listener.chatId)
          )
        }
      })
  }

  static skipThinkingForChat(chatId: number) {
    const listener = this.activeListeners.find((l) => l.chatId === chatId)
    if (listener) {
      listener.builder.skipThinking()
      listener.inProgressMessageStale = true
      listener.flushInProgressMessage()
    }
  }

  private constructor(
    chatId: number,
    streamId: string,
    private dispatch: AppDispatch,
    private sub_type?: BotMessageSubtype
  ) {
    dispatch(userSlice.actions.addStreamId(streamId))
    this.chatId = chatId
    this.streamId = streamId
    this.builder = new StreamedMessageBuilder(sub_type)

    this.eventSource = this.openConnection()
  }

  private openConnection(): EventSource {
    const url = eventStreamUrl({
      chatId: this.chatId,
      streamId: this.streamId,
      lastEventId: this.lastEventId,
    })

    const eventSource = new EventSource(url)

    eventSource.onerror = (event) => this.onError(event)
    eventSource.onmessage = (event: MessageEvent) => {
      try {
        const parsedPayload: ChatServerEventData = JSON.parse(event.data)

        // If we can see that the event source is done, close it now
        // (or it will close on its own, which can trigger onerror),
        // even though the smoother may still be delivering events.
        const streamIsComplete =
          parsedPayload.status === "done" || parsedPayload.status === "error"
        if (streamIsComplete) {
          this.eventSource.close()
        }
        // Determine if this batch of chunks should be delayed by the EventSmoother
        // - Text chunks: delayable (smoothed out)
        // - Anything else (places, ui_signals, delays, ...): non-delayable
        const delayable =
          parsedPayload.status === "in-progress" &&
          parsedPayload.data.every((chunk) => {
            if (chunk.type === "text") return true
            return false
          })

        // A non-delayable batch is delivered right away, so flush the text
        // still queued in the smoother first: chunks must reach the message
        // in the order they streamed. Otherwise a batch like [text "6", delay]
        // shows "Vienna6" ahead of the queued " from December 11–13, 202".
        if (!delayable) {
          this.smoother.flushAll()
        }
        this.smoother.add(
          () => void this.onMessage(event, parsedPayload),
          delayable
        )
        if (streamIsComplete) {
          this.smoother.done()
        }
      } catch (e) {
        // there was some HB's where the event.data was not a valid JSON, not sure what that is but really hard to
        // debug without the actual data, going to include the event.data in the error message
        if (e instanceof SyntaxError) {
          throw new SyntaxError(`${e.message} | event data: ${event.data}`, e)
        }

        throw e
      }
    }

    // Show an empty "..." message if we haven't heard anything from the bot after a bit
    void wait(1000).then(() => {
      if (
        this.eventSource === eventSource &&
        eventSource.readyState !== EventSource.CLOSED
      ) {
        this.dispatch(
          userSlice.actions.addInProgressMessageIfAbsent({
            chatId: this.chatId,
            streamId: this.streamId,
            sub_type: this.sub_type,
          })
        )
      }
    })

    return eventSource
  }

  private reopenConnection() {
    // A pending retry must not revive a listener after the page disconnected it.
    if (this.closed) return
    try {
      this.eventSource.close()
    } catch (e) {
      // ignore
    }
    this.eventSource = this.openConnection()
  }

  close({ preserveStreamIds = false } = {}) {
    if (this.closed) return
    this.closed = true
    if (this.eventSource.readyState !== EventSource.CLOSED) {
      this.eventSource.close()
    }
    if (this.smoother) {
      this.smoother.cancel()
    }
    if (!preserveStreamIds) {
      this.dispatch(userSlice.actions.removeStreamId(this.streamId))
    }
    remove(
      StreamedMessageListener.activeListeners,
      (stream) => stream.streamId === this.streamId
    )
  }

  private async onMessage(
    event: MessageEvent,
    parsedPayload: ChatServerEventData
  ) {
    if (this.closed) return
    const status = parsedPayload.status

    if (status === "in-progress") {
      parsedPayload.data.forEach((chunk) => this.processInProgressChunk(chunk))
      this.flushInProgressMessage()
      this.lastEventId = event.lastEventId
    } else if (status === "canceled") {
      this.dispatch(
        userSlice.actions.stopMessageStreamingWithCancel(this.streamId)
      )
      this.close()
    } else if (status === "done") {
      // In the last event, the stream gives us the complete bot message and
      // the chat's latest chat details, which may have been affected by the
      // conversation. We may also have accumulated itinerary events to pass
      // along for saving.
      const botMessage = parsedPayload.data.bot_message
      botMessage.trip_change = this.builder.tripChange

      if (this.builder.thinkingSkipped) {
        botMessage.thinking_duration_seconds = undefined
        botMessage.has_activity_feed = false
      }

      this.dispatch(
        userSlice.actions.stopMessageStreaming({
          chat: parsedPayload.data.chat,
          finalMessage: botMessage,
        })
      )

      this.close()

      // Check for immediate follow-up flags
      if (botMessage.immediate_followup) {
        // Execute immediately without delay
        void this.dispatch(executeImmediateFollowUp())
      } else {
        // Use existing delayed logic
        void this.dispatch(considerAskingBotForFollowUp())
      }
    } else if (status === "error") {
      console.log(
        `Streaming error (${parsedPayload.data.type}): ${parsedPayload.data.message}`
      )
      if (parsedPayload.data.type === "rate-limit") {
        void this.dispatch(temporarilyDisableMessageStreaming())
        this.close()
      } else if (parsedPayload.data.retryable && this.retryCount < 3) {
        // We do a retry when we get an error event from the server, but
        // unlike the retries that we do for network errors (see onError),
        // these will often just fail again, because streaming for this
        // message is now complete and there's no stream to listen to.
        // There may be a few cases, however, when it's helpful to retry.
        console.log("retrying")
        this.retryCount++
        await wait(1000 * 2 ** this.retryCount)
        this.reopenConnection()
      } else {
        console.log(
          `Streaming error (${parsedPayload.data.type}): ${parsedPayload.data.message}`
        )
        this.dispatch(
          userSlice.actions.stopMessageStreamingWithError(this.streamId)
        )
        this.close()
        console.error(parsedPayload.data.message)
      }
    } else {
      warnUnreachable(status)
    }
  }

  private processInProgressChunk(chunk: MessageChunk) {
    if (this.stopRequested) return

    this.builder.addChunk(chunk)

    if (
      chunk.type === "delay" &&
      chunk.agent_session_id &&
      chunk.agent_session_id !== this.snapshotFetchedForSession
    ) {
      this.snapshotFetchedForSession = chunk.agent_session_id
      void this.fetchAndApplySnapshot(chunk.agent_session_id)
    }

    this.inProgressMessageStale = true

    // Text dispatches nothing of its own. Every other kind might -- now or
    // when someone adds one -- and whatever it dispatches has to see the
    // message this chunk just became part of. Publishing ahead of all of
    // them, rather than ahead of the three that happen to need it today,
    // costs nothing: a non-text chunk ends its batch anyway, so this is the
    // batch's own flush arriving a moment early.
    if (chunk.type !== "text") this.flushInProgressMessage()

    if (chunk.type === "flight_search") {
      void this.dispatch(warmFlightSearchCache(chunk.search_info))
    }
    if (chunk.type === "action") {
      void this.dispatch(
        performBotAction({
          action: chunk.action,
          highlightAffectedEvent: true,
        })
      )
    }
    if (chunk.type === "itinerary_events") {
      this.dispatch((dispatch, getState) => {
        const tripRef = selectTripRef(getState())
        if (tripRef) {
          updateEventsInCache(
            dispatch,
            tripRef,
            chunk.itinerary_events,
            getState
          )
        }
      })
      void this.dispatch(
        showToastsForStreamedEvents({
          events: chunk.itinerary_events,
        })
      )
    }
  }

  // One dispatch per batch of chunks rather than one per chunk. Every chunk
  // rebuilds the whole message, so a batch's earlier builds are states no one
  // ever sees -- but each still ran the reducers and every live useSelector.
  // A chunk with a side effect of its own flushes first, so that side effect
  // still sees the store as it did when each chunk dispatched for itself.
  private flushInProgressMessage() {
    if (!this.inProgressMessageStale) return
    this.inProgressMessageStale = false
    this.dispatch(
      userSlice.actions.updateInProgressMessage({
        chatId: this.chatId,
        streamId: this.streamId,
        message: this.builder.buildInProgressMessage(),
      })
    )
  }

  private async fetchAndApplySnapshot(agentSessionId: number) {
    try {
      const agentApi = new AgentApi(buildClientAxios())
      const snapshot = await agentApi.getSessionStatusSnapshot(agentSessionId)
      if (this.snapshotFetchedForSession !== agentSessionId) return
      if (snapshot?.visualizations?.length) {
        this.builder.applyStatusSnapshot(snapshot, agentSessionId)
        this.inProgressMessageStale = true
        this.flushInProgressMessage()
      }
    } catch {
      // Snapshot fetch failed - not critical, activity feed will populate from next WebSocket update
    }
  }

  private async onError(event: Event) {
    if (this.closed) return
    console.log(
      `event source error: ${JSON.stringify(event, [
        "message",
        "arguments",
        "type",
        "name",
      ])}`
    )
    if (this.retryCount < StreamedMessageListener.MAX_RETRY_COUNT) {
      this.retryCount++
      await wait(1000 * 2 ** this.retryCount)
      this.reopenConnection()
    } else {
      this.dispatch(
        userSlice.actions.stopMessageStreamingWithError(this.streamId)
      )
      this.close()
    }
  }
}
